<script setup lang="ts">
import type { KDropdownItem } from "@kaleidoscope-solutions/ks-design-system"
import { KDropdown, KSelectWrapper } from "@kaleidoscope-solutions/ks-design-system"
import { ArrowLongRightIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { computed } from "vue"
import LocateDropdownItem from "@/components/FileImport/LocateDropdownItem.vue"
import { useListImportStore } from "@/stores/index.js"

type LocateItem = KDropdownItem & { id: number }

interface Props {
  target?: string
  locateItems: LocateItem[]
  locateInfo: Map<string, number | undefined>
  selectedTableHeader?: number
}

interface Emits {
  (event: "update:selectedTableHeader", value: any): void
  (event: "clearSelectedTableHeader"): void
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

const listImportStore = useListImportStore()

const keyTargets = computed(() => {
  return props.locateItems.map((item) => {
    const noValues = listImportStore.isColumnEmpty(item.id)
    const tooltip = noValues ? "No items found in this column" : undefined

    // Do not mutate the existing item as objects are passed by reference
    // and not shallow / deep cloned in a map
    return {
      ...item,
      disabled: noValues,
      tooltip,
    }
  })
})

function handleKeyTypeSelect(attribute: string, item: KDropdownItem | undefined) {
  if (item === undefined) {
    listImportStore.locateInfo.set(attribute, undefined)
    return
  }

  const column = Number(item.id)

  listImportStore.locateInfo.set(attribute, column)
  emit("update:selectedTableHeader", column)
}
function handleKeyTypeFocus(attribute: string) {
  const column = listImportStore.locateInfo.get(attribute)
  emit("update:selectedTableHeader", column)
}
function handleKeyTypeClosed() {
  emit("clearSelectedTableHeader")
}

function isKeyTypeSelected(keyType: string) {
  if (props.selectedTableHeader === undefined) {
    return false
  }

  return listImportStore.locateInfo.get(keyType) === props.selectedTableHeader
}
</script>

<template>
  <div
    v-for="[attribute, value] in locateInfo"
    :key="attribute"
    class="flex flex-row items-center gap-2"
  >
    <KDropdown
      placeholder="Column to import"
      title="Select it from the file"
      :nullable="true"
      :model-value="value"
      :items="keyTargets"
      class="w-52 bg-white"
      @update:model-value="handleKeyTypeSelect(attribute, $event)"
      @focusin="handleKeyTypeFocus(attribute)"
      @closed="handleKeyTypeClosed()"
    >
      <template #selected="{ selected, open, focused }">
        <div class="w-52">
          <KSelectWrapper
            :open="open"
            :is-selected="isKeyTypeSelected(attribute) && focused"
          >
            <span
              v-if="keyTargets[selected]"
              class="font-bold"
            >
              {{ locateItems[selected].label }}
            </span>
            <span
              v-else
              class="text-gray-700"
            >
              Column to import
            </span>
          </KSelectWrapper>
        </div>
      </template>
      <template #item="{ item }">
        <LocateDropdownItem :item="item" />
      </template>
    </KDropdown>

    <ArrowLongRightIcon
      class="size-4 shrink-0 text-black"
    />

    <span class="flex w-full text-base">{{ attribute }}</span>
  </div>
</template>
